import React, { useCallback } from 'react';
import { CharacterData } from '../../types/character';
import ItemBrowser, { GenericItem } from './ItemBrowser';
import './EquipmentBrowser.css';

interface EquipmentBrowserProps {
  characterId?: string;
  category: 'weapons' | 'armor' | 'wheelchairs';
  character?: CharacterData | null;
  onAddToCharacter?: (itemType: string, itemId: string) => Promise<void>;
}

const EquipmentBrowser: React.FC<EquipmentBrowserProps> = ({
  characterId,
  category,
  character,
  onAddToCharacter,
}) => {
  // Helper function for title case formatting
  const toTitleCase = (str: string): string => {
    return str.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };

  // Helper function to get relevant card display info
  const getCardDisplayInfo = (item: GenericItem): string => {
    const itemAny = item as any;
    let parts: string[] = [];

    if (item.category === 'weapon') {
      // For weapons: show subtype, trait, range, and tier
      if (itemAny.subtype) parts.push(toTitleCase(itemAny.subtype));
      if (itemAny.trait) parts.push(toTitleCase(itemAny.trait));
      if (itemAny.range) parts.push(toTitleCase(itemAny.range));
      if (itemAny.tier) parts.push(`Tier ${itemAny.tier}`);
    } else if (item.category === 'armor') {
      // For armor: show type and tier (no subtype in Daggerheart)
      parts.push(toTitleCase(item.type));
      if (itemAny.tier) parts.push(`Tier ${itemAny.tier}`);
    } else if (item.category === 'wheelchair') {
      // For wheelchair: show frame and tier
      if (itemAny.frame) parts.push(toTitleCase(`${itemAny.frame} Frame`));
      if (itemAny.tier) parts.push(`Tier ${itemAny.tier}`);
    }

    return parts.length > 0 ? parts.join(' • ') : toTitleCase(item.type);
  };

  // Helper function to get default tiers based on character
  const getDefaultTiers = (): number[] => {
    if (character?.tier) {
      // Return character tier + all lower tiers
      return Array.from({ length: character.tier }, (_, i) => i + 1);
    }
    // Default to Tier 1 if no character
    return [1];
  };

  // Load equipment data
  const loadEquipment = useCallback(async (): Promise<GenericItem[]> => {
    // Load all equipment files
    const [weaponsData, armorData, wheelchairData] = await Promise.all([
      import('../../data/equipment/weapons.json'),
      import('../../data/equipment/armor.json'),
      import('../../data/equipment/wheelchairs.json'),
    ]);

    // Extract equipment from nested structure and flatten
    const weaponItems = Object.values(weaponsData.primaryWeapons).flat().concat(
      Object.values(weaponsData.secondaryWeapons || {}).flat()
    ).map((item: any, index: number) => ({
      ...item,
      id: item.id || `weapon-${index}`,
      name: toTitleCase(item.name || 'Unknown Weapon'),
      description: item.description || item.feature || '',
      category: 'weapon',
      originalCategory: item.category,
      type: 'weapon' as const,
      tags: [item.subtype, item.trait, item.burden].filter(Boolean),
      equipped: false,
      modifiers: item.modifiers || [],
    }));

    const armorItems = Object.values(armorData.armor).flat().map((item: any, index: number) => ({
      ...item,
      id: item.id || `armor-${index}`,
      name: toTitleCase(item.name || 'Unknown Armor'),
      description: item.description || item.feature || '',
      category: 'armor',
      type: 'armor' as const,
      tags: [item.tier ? `Tier ${item.tier}` : ''].filter(Boolean),
      equipped: false,
      modifiers: item.modifiers || [],
    }));

    const wheelchairItems = Object.values(wheelchairData.wheelchairs).flat().map((item: any, index: number) => ({
      ...item,
      id: item.id || `wheelchair-${index}`,
      name: toTitleCase(item.name || 'Unknown Wheelchair'),
      description: item.description || item.feature || '',
      category: 'wheelchairs',
      type: 'wheelchair' as const,
      tags: [item.subtype, item.trait, item.burden, item.frame].filter(Boolean),
      equipped: false,
      modifiers: item.modifiers || [],
    }));

    // Filter by category
    const categoryMap = {
      'weapons': 'weapon',
      'armor': 'armor',
      'wheelchairs': 'wheelchairs',
    };
    const itemCategory = categoryMap[category];

    const allEquipment = [
      ...weaponItems,
      ...armorItems,
      ...wheelchairItems,
    ];

    return allEquipment.filter(item => item.category === itemCategory);
  }, [category]); // Only depends on category

  // Helper function to get category display name
  const getCategoryDisplayName = (cat: string): string => {
    switch (cat) {
      case 'weapons':
        return 'Weapons';
      case 'armor':
        return 'Armor';
      case 'wheelchairs':
        return 'Wheelchairs';
      default:
        return 'Equipment';
    }
  };

  return (
    <ItemBrowser
      loadItems={loadEquipment}
      category={category}
      itemType="equipment"
      libraryTitle={getCategoryDisplayName(category)}
      characterId={characterId}
      character={character}
      onAddToCharacter={onAddToCharacter}
      showTierFilter={true}
      showLevelFilter={false}
      getDefaultTiers={getDefaultTiers}
      getCardDisplayInfo={getCardDisplayInfo}
    />
  );
};

export default EquipmentBrowser;